import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { getFocusableTreeWalker } from '@react-aria/focus';
import { usePresence } from 'framer-motion';
import { useEffect, useImperativeHandle, useRef, useState, useLayoutEffect } from 'react';
// import { DismissButton, OverlayContainer, FocusScope } from 'react-aria';
import { OverlayContainer, FocusScope, usePreventScroll, useModal, useDialog } from 'react-aria';

import { mergeRefs } from '../../lib/mergeRefs';

import { useCurrentProps } from '../../hooks/useCurrentProps';

import { animationFade } from '../../theme/animations/animationFade';
import { cssUtil } from '../../theme/cssUtil';
import { su } from '../../theme/functions/su';
import { motion } from '../../theme/motion';
import { theme } from '../../theme/theme';

import { overlayCloseReasons } from './overlayCloseReasons';
import { OverlayContext } from './OverlayContext';
import { OverlaySource } from './OverlaySource';
import { OverlayStackContext } from './OverlayStackContext';
import { useOverlayStackContext } from './OverlayStackContext';
import { useOverlay } from './useOverlay';
import { usePreserveOpenModality } from './usePreserveOpenModality';

export function Dialog(props) {
  const [isPreventCloseOverride, setPreventCloseOverride] = useState(null);
  const [stackIndex, setStackIndex] = useState(-1);

  const overlayContext = useCurrentProps({ setPreventCloseOverride, stackIndex });

  // Ensure pressing Escape to dismiss a mouse-opened dialog doesn't leave a focus ring on the
  // trigger (or on the item still focused inside it during the exit animation).
  const preserveModalityOnClose = usePreserveOpenModality({ enabled: props.restoreFocus ?? true });

  const overlayRef = useRef();
  const rootRef = useRef();
  const instanceRef = useRef({
    didFocus: false,
    isPreventClose: isPreventCloseOverride,
    onCloseRequest: onCloseRequestMiddleWare,
  });

  function onCloseRequestMiddleWare(reason) {
    if (isPreventCloseOverride) {
      return;
    }

    preserveModalityOnClose(reason);

    if (props.onClose != null) {
      // If something wants to respond different to close behavior.
      // E.g. click outside = save or escape = cancel.
      // They are responsible for calling overlayTriggerState.close()
      props.onClose?.(reason);
      return;
    }

    // Stack removal happens in useLayoutEffect cleanup.
    props.overlayTriggerState?.close();
  }

  useLayoutEffect(() => {
    instanceRef.current.onCloseRequest = onCloseRequestMiddleWare;
    instanceRef.current.isPreventClose = isPreventCloseOverride;
  });

  // Lets a dialog that handles Escape itself, because a child stops the key from reaching the
  // dialog, still close through this path with the matching reason.
  useImperativeHandle(
    props.closeRequestRef,
    () => {
      return function requestClose(reason) {
        instanceRef.current.onCloseRequest(reason);
      };
    },
    [],
  );

  const { context, isRoot } = useOverlayStackContext();

  useLayoutEffect(() => {
    const source = new OverlaySource({
      ref: overlayRef,
      onCloseRequest(reason) {
        instanceRef.current.onCloseRequest(reason);
      },
      getIsPreventClose() {
        return instanceRef.current.isPreventClose;
      },
    });

    const length = context.stack.push(source);
    setStackIndex(length - 1);

    return function () {
      // It might have been removed already onCloseRequest
      // console.log('current:stack', [...context.stack])
      const index = context.stack.findIndex((element) => element === source);

      if (index > -1) {
        context.stack.splice(index, 1);
        // console.log('next:stack', [...context.stack])
      }
    };
  }, [context]);

  const overlay = useOverlay(
    {
      context,
      isOpen: props.overlayTriggerState?.isOpen ?? true,
      isKeyboardDismissDisabled: false,
      onCloseRequest: (reason) => {
        // This one fires for keyboard.
        onCloseRequestMiddleWare(reason);
      },
    },
    overlayRef,
  );

  usePreventScroll();

  const modal = useModal();
  const dialog = useDialog(
    {
      role: props.role ?? 'dialog',
      'aria-label': props['aria-label'] ?? 'Dialog',
    },
    overlayRef,
  );

  const [isPresent, safeToRemove] = usePresence();

  useEffect(() => {
    // ${motion.duration.normal}
    !isPresent && setTimeout(safeToRemove, 350);
  }, [isPresent, safeToRemove]);

  let [tabIndex, setTabIndex] = useState(undefined);

  const style = {
    '--stack-index': stackIndex,
  };

  // Check if the overlay has a focusable child. If not we make the overlay itself focusable so it
  // can be closed with the keyboard.
  useLayoutEffect(() => {
    if (overlayRef.current) {
      const update = () => {
        // Detect if there are any tabbable elements and update the tabIndex accordingly.
        let walker = getFocusableTreeWalker(overlayRef.current, { tabbable: true });
        setTabIndex(walker.nextNode() ? undefined : 0);
      };

      update();

      // Update when new elements are inserted, or the tabIndex/disabled attribute updates.
      const observer = new MutationObserver(update);
      observer.observe(overlayRef.current, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['tabIndex', 'disabled'],
      });

      return () => {
        observer.disconnect();
      };
    }
  }, []);

  // If autoFocus was enabled and we set the tabIndex to the overlay make ensure we focus it. But
  // only once during the lifetime of this component.
  useLayoutEffect(() => {
    if (props.autoFocus === true && tabIndex != null && instanceRef.current.didFocus === false) {
      instanceRef.current.didFocus = true;
      overlayRef.current.focus();
    }
  }, [props.autoFocus, tabIndex]);

  function wrap(children) {
    if (isRoot === true) {
      return (
        <OverlayStackContext.Provider value={context}>{children}</OverlayStackContext.Provider>
      );
    }

    return children;
  }

  return wrap(
    <OverlayContext.Provider value={overlayContext}>
      <OverlayContainer>
        <Dialog.Root ref={rootRef} data-is-open={isPresent} data-is-exiting={isPresent === false}>
          <FocusScope
            restoreFocus={props.restoreFocus ?? true}
            contain={props.containFocus ?? true}
            autoFocus={props.autoFocus ?? true}
          >
            <Dialog.Content
              {...overlay.overlayProps}
              {...dialog.dialogProps}
              {...modal.modalProps}
              ref={mergeRefs([overlayRef, props.overlayRef])}
              style={style}
              data-stack-index={stackIndex}
              tabIndex={tabIndex}
            >
              <Dialog.Children>{props.children}</Dialog.Children>
            </Dialog.Content>
          </FocusScope>
        </Dialog.Root>
      </OverlayContainer>
    </OverlayContext.Provider>,
  );
}

Dialog.Content = styled.div`
  position: relative;
  outline: 0;
`;

Dialog.Children = styled.div`
  display: flex;
  flex-direction: column;

  max-width: calc(100vw - ${su(4)});
  max-height: calc(100vh - ${su(4)});
  background-color: ${theme.color.component};
  border-radius: ${theme.borderRadius.medium};
  box-shadow: ${theme.boxShadow.default};
  outline: 0;
`;

Dialog.Root = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  background: ${theme.color.overlay};
  z-index: calc(${theme.zIndex.overlay} + var(--stack-index, 0));

  &[data-is-open='true'] {
    animation: ${animationFade.in} ${motion.duration.normal} ease-out forwards;

    &:not(${cssUtil.isReducedMotion}) {
      ${Dialog.Background}, ${Dialog.Children} {
        animation: ${keyframes`
          0% {
            transform: translate(var(--translate-x, 0), var(--translate-y, 0)) scale(0.8);
            animation-timing-function: ease-out;
          }
          60% {
            transform: translate(var(--translate-x, 0), var(--translate-y, 0)) scale(1.05);
            animation-timing-function: ease-in-out;
          }
          100% {
            transform: translate(var(--translate-x, 0), var(--translate-y, 0)) scale(1);
          }
        `} ${motion.duration.normal} ease-out none;
      }
    }
  }

  &[data-is-exiting='true'] {
    animation: ${animationFade.out} ${motion.duration.normal} ease-out forwards;

    &:not(${cssUtil.isReducedMotion}) {
      ${Dialog.Background}, ${Dialog.Children} {
        animation: ${keyframes`
          0% {
            transform: translate(var(--translate-x, 0), var(--translate-y, 0)) scale(1);
            animation-timing-function: ease-out;
          }
          40% {
            transform: translate(var(--translate-x, 0), var(--translate-y, 0)) scale(1.05);
            animation-timing-function: ease-in;
          }
          100% {
            transform: translate(var(--translate-x, 0), var(--translate-y, 0)) scale(0.8);
          }
        `} ${motion.duration.normal} ease-out none;
      }
    }
  }
`;

Dialog.REASON = overlayCloseReasons;
